import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MAIN_SITE_URL } from '@/mocks/site';

export default function SafetyFaqSection() {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const checks = [
    { icon: 'ri-global-line', label: t('home.safety.check1') },
    { icon: 'ri-lock-line', label: t('home.safety.check2') },
    { icon: 'ri-key-2-line', label: t('home.safety.check3') },
    { icon: 'ri-shield-keyhole-line', label: t('home.safety.check4') },
  ];

  const faqs = [
    { q: t('home.faq.q1'), a: t('home.faq.a1') },
    { q: t('home.faq.q2'), a: t('home.faq.a2') },
    { q: t('home.faq.q3'), a: t('home.faq.a3') },
    { q: t('home.faq.q4'), a: t('home.faq.a4') },
    { q: t('home.faq.q5'), a: t('home.faq.a5') },
    { q: t('home.faq.q6'), a: t('home.faq.a6') },
  ];

  return (
    <>
      {/* Safety */}
      <section id="safety" className="bg-secondary-50">
        <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-800">
                <i className="ri-shield-check-line text-sm" />
                {t('common.nav.safety')}
              </span>
              <h2 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                {t('home.safety.title')}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-foreground-600">{t('home.safety.text')}</p>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {checks.map((check) => (
                <div key={check.label} className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                    <i className={`${check.icon} text-base`} />
                  </span>
                  <span className="text-sm font-medium text-foreground-800">{check.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-background-50">
        <div className="mx-auto max-w-3xl px-4 py-14 md:px-6 md:py-20">
          <div className="text-center">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{t('home.faq.title')}</h2>
            <p className="mt-3 text-base text-foreground-600">{t('home.faq.subtitle')}</p>
          </div>

          <div className="mt-8 flex flex-col gap-3">
            {faqs.map((faq, i) => {
              const isOpen = openFaq === i;
              return (
                <div key={faq.q} className="overflow-hidden rounded-lg border border-background-200 bg-background-100">
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer"
                  >
                    <span className="font-heading text-base font-semibold text-foreground-950">{faq.q}</span>
                    <span
                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-background-200 text-foreground-600 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                    >
                      <i className="ri-arrow-down-s-line text-base" />
                    </span>
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-4">
                      <p className="text-sm leading-relaxed text-foreground-600">{faq.a}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden bg-primary-500">
        <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-background-50/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-16 h-64 w-64 rounded-full bg-background-50/10 blur-3xl" />
        <div className="relative mx-auto max-w-3xl px-4 py-16 text-center md:px-6 md:py-20">
          <h2 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">{t('home.final.title')}</h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-background-50/90">{t('home.final.text')}</p>
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="mt-7 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-6 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-background-100"
          >
            {t('common.cta.explore')}
            <i className="ri-arrow-right-up-line text-base" />
          </a>
        </div>
      </section>
    </>
  );
}